/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../../share/util/individualDiagnose';
import config from '../../../../../share/config';
import customTable from '../../../../../share/components/vue/PaginationTable';
import projectData from '../../../../../share/components/vue/ProjectData';
import diagnoseResource from '../../../../../share/util/diagnose';
import shareVehicle from '../../../../../share/util/vehicle';
import fileExport from '../../../../../util/fileExport';
import vinInfoUtils from '../../../../../util/vinInfoUtils';
import { apiErrorDefaultAction } from '../../../../../share/util/api/errorAction';
import { PHASE, CONTROL_HISTORY_TYPE_LIST, PHASE_INFO } from './constants';
import { read } from './read';
import { save } from './save';
import { exit } from './exit';
import { createColumns, createDisplayData, createFdColumns, createDisplayFdData } from './table';

export default {
  name: 'requirementSpES9',
  data() {
    return {
      phase: PHASE.START,                                                       // 画面表示用フェーズ
      systemId: '',                                                             // システムID
      isLoading: false,                                                         // スピナー表示用
      wallDisplayInfo: [],                                                      // 一覧表示データ
      tableColumns: [],                                                         // 一覧表示カラム情報
      displayFdInfo: [],                                                        // 詳細FD表示データ
      tableFdColumns: [],                                                       // 詳細FDカラム情報
      preFdData: {},                                                            // データ保持用(車線逸脱抑制(構造物)作動履歴一覧)
      preDetailFdData: {},                                                      // データ保持用(詳細FD)
      statusValue: '',                                                          // 詳細FDに表示するステータス
      statusLabel: '',                                                          // 詳細FDに表示するステータスラベル
      vin: null,                                                                // プロジェクトデータ保存画面のVIN/車両番号
      vehicleName: null,                                                        // プロジェクトデータ保存画面の車両
      displayProjectID: '',                                                     // プロジェクトデータ保存画面のID
      hideRegistrationNumber: true,                                             // プロジェクトデータ保存画面の登録番号表示フラグ
      registrationNumber: '',                                                   // プロジェクトデータ保存画面の登録番号表示
      diagnoseComment: '',                                                      // プロジェクトデータ保存画面の診断コメント
      functionName: 'individual.individual_diagnosis_function_special_feature', // プロジェクトデータ保存画面の機能名
      dataName: '',                                                             // プロジェクトデータ保存画面のデータ名
      preDataName: '',                                                          // プロジェクトデータ保存画面の保持用データ名
      dataNameSuffix: 0,                                                        // プロジェクトデータ保存画面の連番
      memo: '',                                                                 // プロジェクトデータ保存画面のメモ
      preMemo: '',                                                              // プロジェクトデータ保存画面の保持用メモ
      controlHistoryType: '',                                                   // 作動履歴区分設定
      controlHistoryTypeList: [],                                               // 作動履歴区分プルダウンリスト
      selectedId: '',                                                           // 作業履歴区分プルダウン選択値
      modal_special_requirement_ES9: false,
      isSession: false,                                                         // セッション遷移の状態監視
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
    additionalInfo: { type: Object, required: true }
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId,
      vehicleInfo: state => state.vehicle.vehicleInfo,
      vehicleModelDisplayNameList: state => state.masterResources.vehicleModelDisplayNameList,
      language: state => state.systems.language,
    }),

    // 診断系のAPIは複数画面から使用されているため
    // 現在の画面で使用されるAPIが診断/データ再生/個別診断のうちどの機能なのか判別する
    operationScreen() {
      return diagnoseResource.operationFrom(this.$router.currentRoute.value.name);
    },

    /** ダイアログのヘッダー色 */
    headerColor() { return PHASE_INFO[this.phase]['headerColor']; },

    /** ダイアログのヘッダー文言 */
    headerTitle() { return this.$t(PHASE_INFO[this.phase]['headerTitle']); },

    /** キャンセルボタン表示有無 */
    showButtonCancel() {
      const showCancelButtonMap = {
        [PHASE.LIST_VIEW]: true,
        [PHASE.NODATA]: true,
        [PHASE.PROJECT_DATA]: true,
      };
      let result = showCancelButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },

    /** 保存ボタン表示有無 */
    showButtonSave() {
      const showSaveButtonMap = {
        [PHASE.LIST_VIEW]: true,
        [PHASE.NODATA]: true,
        [PHASE.PROJECT_DATA]: true,
      };
      let result = showSaveButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },

    /** 戻るボタン表示有無 */
    showButtonBack() {
      return this.phase === PHASE.DETAILED_FD;
    },

    /** OKボタン表示有無 */
    showButtonOk() {
      return this.phase === PHASE.FAILED;
    },

    /** キャンセルボタン用クラス設定 */
    cancelButtonClass() {
      const cancelButtonClassMap = {
        [PHASE.LIST_VIEW]: 'mx-2 mt-2',
        [PHASE.NODATA]: 'mx-2 mt-2',
      };
      let result = cancelButtonClassMap[this.phase];
      if (!result) {
        // 合致しない場合はデフォルトのクラスを返す。
        result = 'mx-2';
      }
      return result;
    },

    /** 保存ボタン用クラス設定 */
    saveButtonClass() {
      const saveButtonClassMap = {
        [PHASE.LIST_VIEW]: 'mx-2 mt-2',
        [PHASE.NODATA]: 'btn btn-secondary mx-2 mt-2',
      };
      let result = saveButtonClassMap[this.phase];
      if (!result) {
        // 合致しない場合はデフォルトのクラスを返す。
        result = 'mx-2';
      }
      return result;
    },

    /** 保存ボタン活性/非活性設定 */
    isDisabledSave() {
      if (this.phase === PHASE.NODATA) {
        // 履歴なし表示画面では常にボタンを非活性
        return true;
      } else {
        return this.isLoading;
      }
    },

    /** ダイアログのサイズ設定 */
    modalSize() {
      switch (this.phase) {
        case PHASE.DETAILED_FD:
          return 'huge';
        default:
          return 'lg';
      }
    },
  },

  components: {
    'custom-table': customTable,
    'project-data': projectData
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_special_requirement_ES9 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);

      // リソース(作動履歴_DID)のi18nキー取得
      this.i18nSpwsDidKey = `controlHistory${this.systemId}`;

      // 作動履歴区分リストの生成
      this.controlHistoryTypeList = this.getControlHistoryTypeList();

      // サポート情報から作動履歴区分を設定(優先順のため、0番目から取得)
      this.controlHistoryType = this.controlHistoryTypeList[0].control_history_type_id;

      // プルダウン初期値設定
      this.selectedId = this.controlHistoryType;

      // プロジェクト保存画面、詳細FDエクスポート時の登録番号の表示有無を判定
      const vehicle = this.vehicleInfo.vehicle;
      if (vehicle.vin.type === config.VEHICLEINFO_TYPE_VIN) {
        this.hideRegistrationNumber = false;
      }
    },

    /** メモ情報、データ名のクリア */
    clear() {
      this.diagnoseComment = '';
      [this.dataName, this.preDataName] = [this.preDataName, this.dataName];
      [this.memo, this.preMemo] = [this.preMemo, this.memo];
    },

    /** Wizardを閉じる */
    closeWizard() {
      this.$emit('close-wizard');
      this.modal_special_requirement_ES9 = false;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /**
     * 一覧表示画面 FDボタン押下
     * @param {Object} row 選択行
     */
    rowButtonClickEvent(row) {
      // 詳細FD画面に表示する値設定
      createFdColumns(this, row.unique_status);
      createDisplayFdData(this, row.unique_status);
      this.statusLabel = this.$t('message.header_vehicle_dtc_table_status') + '：';
      this.statusValue = this.$t(`${this.i18nWsKey}.${row.unique_status}`);
      this.phase = PHASE.DETAILED_FD;
    },

    /** 一覧表示画面 保存ボタン押下 */
    screenMovement() {
      // プロジェクト保存画面の入力情報初期化
      this.clear();

      const vehicle = this.vehicleInfo.vehicle;
      // プロジェクト保存画面に必要な情報を設定
      this.vin = vehicle.vin.vin;
      this.displayProjectID = vehicle.project.id;

      // 車種情報から表示名称を取得
      const spec = shareVehicle.createModelDisplaySpec(vehicle.vin.type, vehicle.spec);
      this.vehicleName = shareVehicle.createModelDisplayNameCurrentLanguage(this.vehicleInfo, this.vehicleModelDisplayNameList, this.language, '{0} {1}', spec);
      // 海外VINの場合、登録番号は画面から非表示にする
      if (this.hideRegistrationNumber) {
        if (vinInfoUtils.outputRegistrationNumber(vehicle.vin)) {
          this.registrationNumber = vinInfoUtils.createRegistrationNumber(vehicle.vin);
        }
        else {
          this.registrationNumber = '';
        }
      }
      this.phase = PHASE.PROJECT_DATA;
    },

    /**
     * 一覧表示画面 モード切替
     * @param {string} val プルダウン選択値（作動履歴区分ID）
     */
    async changeControlHistoryType(val) {
      // データ名、メモ初期化
      this.dataName = this.memo = this.preMemo = '';

      // プルダウン選択値を設定
      this.selectedId = val;
      this.controlHistoryType = val;
      // 読み出しAPI実行
      await read(this);
    },

    /** 詳細FD表示画面 ファイルエクスポートボタン押下 */
    async csvExport() {
      try {
        this.isLoading = true;
        await fileExport.detailFdToCsv(this);
      } finally {
        this.isLoading = false;
      }
    },

    /** 詳細FD表示画面 戻るボタン押下 */
    back() {
      // 画面再描画の為に再度以下関数を呼び出す
      createColumns(this);
      createDisplayData(this);
      this.phase = PHASE.LIST_VIEW;
    },

    /** プロジェクトデータ保存画面 キャンセルボタン押下 */
    callListView() {
      // プロジェクト保存画面の入力情報初期化
      this.clear();
      // 画面再描画の為に再度以下関数を呼び出す
      createColumns(this);
      createDisplayData(this);
      this.phase = PHASE.LIST_VIEW;
    },

    /** プロジェクトデータ保存画面 保存ボタン押下 */
    async callSaveEvent() {
      if (this.$refs.projectData.validate()) {
        await save(this);
      }
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 共通エラー表示
      this.phase = PHASE.FAILED;
    },

    /** キャンセルボタン押下時の処理 */
    onCancelClicked() {
      switch (this.phase) {
        // 一覧表示
        // 履歴データなし表示
        case PHASE.LIST_VIEW:
        case PHASE.NODATA:
          this.closeWizard();
          break;
        // プロジェクトデータ保存  
        case PHASE.PROJECT_DATA:
          this.callListView();
          break;
        default:
          break;
      }
    },

    /** 保存ボタン押下時の処理 */
    async onSaveClicked() {
      switch (this.phase) {
        // 一覧表示
        case PHASE.LIST_VIEW:
          this.screenMovement();
          break;
        // プロジェクトデータ保存  
        case PHASE.PROJECT_DATA:
          await this.callSaveEvent();
          break;
        default:
          break;
      }
    },

    /** 戻るボタン押下時の処理 */
    onBackClicked() {
      // 詳細FD表示  
      if (this.phase === PHASE.DETAILED_FD) {
        this.back();
      }
    },

    /** OKボタン押下時の処理 */
    async onOkClicked() {
      // 共通エラー  
      if (this.phase === PHASE.FAILED) {
        await exit(this);
      }
    },

    /**
     * 作動履歴区分リストの生成
     * @returns {object} setList 作動履歴区分リスト
     * @property {string} control_history_type_id 作動履歴区分ID
     * @property {string} control_history_type_name 作動履歴区分名称
     * @property {boolean} control_history_type_supported 作動履歴区分サポート状態
     */
    getControlHistoryTypeList() {
      const setList = [];
      CONTROL_HISTORY_TYPE_LIST.forEach((itemId) => {
        const setObj = {};
        // サポート状態、名称を取得
        switch (itemId) {
          case 'wall_avoid':
            setObj.control_history_type_supported = this.additionalInfo.is_wall_avoid_supported;
            setObj.control_history_type_name = this.$t(this.i18nWsKey + '.road_structures_button');
            break;
          default:
            break;
        }
        setObj.control_history_type_id = itemId;  // 作動履歴区分ID
        // 配列に追加
        setList.push(setObj);
      });
      // サポート済の作動履歴区分のみ取得
      return setList.filter(item => item.control_history_type_supported === true);
    },
  },

  async mounted() {
    this.init();
    // データ読み出し
    await read(this);
  },
};